import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { bulkJobStatusEndpoint } from "@/config/endpoints";

export interface BulkJobStatus {
  jobId: string;
  status:
    | "waiting"
    | "active"
    | "completed"
    | "failed"
    | "delayed"
    | "paused"
    | "stuck"
    | string;
  progress: { done: number; total: number };
  summary: {
    success: number[];
    skipped: { leadId: number; reason: string }[];
    failed: { leadId: number; reason: string }[];
    totalRequested: number;
  } | null;
  failedReason: string | null;
}

const TERMINAL_STATES = new Set(["completed", "failed"]);

/**
 * Poll the bulk-update job until it reaches a terminal state.
 * Pass `enabled: false` (or `jobId` falsy) to pause the poll —
 * useful when no async job is in flight.
 */
export function useBulkJobStatus(jobId: string | null | undefined) {
  return useQuery<BulkJobStatus>({
    queryKey: ["bulk-job-status", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      try {
        const res = await axiosInstance.get(bulkJobStatusEndpoint(String(jobId)));
        return res.data?.job as BulkJobStatus;
      } catch (err) {
        // Job record gone from Redis (retention expired, or it was enqueued
        // before a deploy). Treat as terminal instead of polling 404s forever.
        const httpStatus = (err as { response?: { status?: number } })
          ?.response?.status;
        if (httpStatus === 404) {
          return {
            jobId: String(jobId),
            status: "completed",
            progress: { done: 0, total: 0 },
            summary: null,
            failedReason: null,
          } as BulkJobStatus;
        }
        throw err;
      }
    },
    refetchInterval: (q) => {
      const data = q.state.data as BulkJobStatus | undefined;
      if (!data) return 3_000;
      return TERMINAL_STATES.has(data.status) ? false : 3_000;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
}
